'use client';

import type { RdsCdcCheckScope, SqlSetupCheckResult } from '@repo/api-gw-sdk';
import { SqlSetupCheckStatus } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

// The verdict endpoint reads a cached value and answers in milliseconds, so it can be polled far
// tighter than check-cdc-prerequisites, whose live AWS checks take seconds per call.
const VERDICT_POLL_INTERVAL_MS = 1500;

// Watchdog for a run whose verdict never registers (e.g. the workflow dies before persisting any
// status): without a bound the dialog would verify and poll forever. A local duration, so browser
// clock skew cannot affect it. A healthy run reports within seconds.
const VERIFY_WATCHDOG_MS = 120_000;

// Whether a verdict belongs to the run this dialog started. A run whose id has not come back yet has
// nothing to match against, so no verdict is attributable to it.
const isVerdictForRun = (check: SqlSetupCheckResult | null | undefined, runId: string | null) => Boolean(runId) && check?.runId === runId;
export interface CdcCheckRun {
  /** The scope's latest verdict, whoever started the run that produced it. */
  verdict: SqlSetupCheckResult | null | undefined;
  /**
   * Last verdict that was not IN_PROGRESS. The footer follows this rather than the live status: a
   * re-verification flips the status to IN_PROGRESS, and deriving from it directly makes "Verify
   * again" vanish and the primary button change text mid-run.
   */
  terminalStatus: SqlSetupCheckStatus | undefined;
  isVerifying: boolean;
  /** True until the first verdict fetch settles; the footer's shape follows the loaded verdict. */
  isLoading: boolean;
  /** True once this hook's own run has landed a verdict, and only for the render it lands on. */
  hasVerdictForThisRun: boolean;
  /** Workflow that carries this hook's run, null until one was started from here. */
  workflow: {
    id: string;
    namespace: string;
  } | null;
  /** Starts a run at this scope. Errors reach the customer through the snackbar. */
  start: () => Promise<void>;
}

/**
 * Drives one scope of the CDC verification from a step dialog: starts a run, follows the cheap verdict
 * key until that run's own verdict lands, and reports the step's terminal status.
 *
 * Attribution is the whole difficulty. The check reports IN_PROGRESS for a moment after the request
 * returns, so status alone would end the run before it even registers, and the previous run's stored
 * verdict would otherwise be read as this one's outcome.
 */
export const useCdcCheckRun = (resourceId: string, scope: RdsCdcCheckScope): CdcCheckRun => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const [isStarting, setIsStarting] = useState(false);
  // Run id of the run started from this dialog (null = no run live, empty until the start request
  // returns one).
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<{
    id: string;
    namespace: string;
  } | null>(null);
  const {
    data,
    isLoading,
    mutate: refreshVerdict
  } = dal.inventory.privateLink.useCdcSqlSetupCheck(resourceId, scope, {
    refreshInterval: latest => {
      const check = latest?.sqlSetupCheck;
      const isRunning = check?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS;
      // Keep polling from the click until this run's own verdict arrives: the reported status alone
      // would stop the poll before the run it belongs to even registers.
      const awaitingThisRun = startedRunId !== null && !isVerdictForRun(check, startedRunId);
      return isRunning || awaitingThisRun ? VERDICT_POLL_INTERVAL_MS : 0;
    }
  });
  const verdict = data?.sqlSetupCheck;
  const isServerVerifying = verdict?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS;
  const [lastTerminalStatus, setLastTerminalStatus] = useState<SqlSetupCheckStatus | undefined>(undefined);
  useEffect(() => {
    const status = verdict?.status;
    if (status && status !== SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS) {
      setLastTerminalStatus(status);
    }
  }, [verdict?.status]);
  const terminalStatus = isServerVerifying ? lastTerminalStatus : verdict?.status ?? lastTerminalStatus;

  // A verdict from a previous run must not end this one's spinner.
  const hasVerdictForThisRun = startedRunId !== null && !isServerVerifying && isVerdictForRun(verdict, startedRunId);
  const isVerifying = startedRunId !== null ? !hasVerdictForThisRun : isServerVerifying;

  // Only the window before the run registers server-side: once IN_PROGRESS is stored, the server's
  // own bounds (run timeout, TTL) end the run, and a slow-but-live run must not be cut off here.
  const isRunAwaitingVerdict = startedRunId !== null && isVerifying && !isServerVerifying;
  useEffect(() => {
    if (!isRunAwaitingVerdict) {
      return;
    }
    const watchdog = setTimeout(() => {
      setStartedRunId(null);
      snackbar.showErrorMessage('Verification timed out. Try again, or contact support if it keeps happening');
    }, VERIFY_WATCHDOG_MS);
    return () => clearTimeout(watchdog);
  }, [isRunAwaitingVerdict, snackbar]);

  // Verification is asynchronous: this starts it and the verdict poll picks the result up. Marking the
  // run live before the request rather than after it is what makes the spinner appear on the click
  // instead of one round trip later.
  const start = useCallback(async () => {
    setIsStarting(true);
    setStartedRunId('');
    try {
      const {
        workflowId,
        workflowNamespace,
        runId
      } = await dal.inventory.privateLink.verifyRdsCdcSqlSetup(resourceId, scope);
      setStartedRunId(runId ?? '');
      setWorkflow(workflowId && workflowNamespace ? {
        id: workflowId,
        namespace: workflowNamespace
      } : null);
      await refreshVerdict();
    } catch (error) {
      setStartedRunId(null);
      setWorkflow(null);
      snackbar.showErrorMessage(await extractErrorMessage(error));
    } finally {
      setIsStarting(false);
    }
  }, [dal, refreshVerdict, resourceId, scope, snackbar]);
  return {
    verdict,
    terminalStatus,
    isVerifying: isStarting || isVerifying,
    isLoading,
    hasVerdictForThisRun,
    workflow,
    start
  };
};